import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { base_url } from "../../utils/axiosConfig";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaTimes, FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// List of restricted states and cities
const restrictedLocations = [
  // "Karnataka",
  // "Belagavi",
  // "Bengaluru",
  // "Ballari",
  // "Mangaluru",
  // "Mysuru",
  // "Visakhapatnam",
  // "Pune",
  // "Nashik",
  // "Nagpur",
  // "Ahmedabad",
  // "Delhi",
  // "Kerala",
];

// Static list of union territories in India
const unionTerritoriesList = [
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli",
  "Daman and Diu",
  "Delhi",
  "Lakshadweep",
  "Puducherry",
  "Jammu and Kashmir",
  "Ladakh",
];

const ChannelPartnerSignUp = ({ show, onHide }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(show);
  const [categories, setCategories] = useState([]);
  const [showThankYou, setShowThankYou] = useState(false);
  const [applicationType, setApplicationType] = useState(null);
  const [isSelectedTip, setIsSelectedTip] = useState(false);
  const [loading, setLoading] = useState({
    categories: false,
    countries: false,
    states: false,
    districts: false,
    pincodes: false,
  });
  const [formData, setFormData] = useState({
    category: null,
    country: null,
    countryStatus: null,
    state: null,
    stateStatus: null,
    district: null,
    districtStatus: null,
    isMetro: false,
    pincode: null,
    pincodeStatus: null,
  });
  const [options, setOptions] = useState({
    countries: [],
    states: [],
    unionTerritories: [],
    districts: [],
    pincodes: [],
  });
  // State for filter and search in step 2
  const [filterType, setFilterType] = useState("all"); // Options: "all", "states", "unionTerritories"
  const [searchQueryState, setSearchQueryState] = useState("");
  const [searchQueryDistrict, setSearchQueryDistrict] = useState("");
  const [searchQueryPincode, setSearchQueryPincode] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [paymentData, setPaymentData] = useState(null);

  const partnerType =
    window.location.pathname === "/signup/channelpartnerplus"
      ? "channelPartnerPlus"
      : "channelPartner";

  const isPlusMode = partnerType === "channelPartnerPlus";
  const [showFormModal, setShowFormModal] = useState(isPlusMode); // open form directly in plus mode
  const [currentStep, setCurrentStep] = useState(isPlusMode ? -1 : 0);
  // Reference to set Formik field value
  const setFieldValueRef = useRef(null);

  // Handle modal close
  const handleClose = () => {
    setShowModal(false);
    onHide();
  };

  // Check if the current step's selection is available for applying
  const isCurrentStepAvailable = () => {
    switch (currentStep) {
      case 0:
        return formData.category !== null;
      case 1:
        return formData.country && formData.countryStatus === "available";
      case 2:
        return formData.state && formData.stateStatus === "available";
      case 3:
        return formData.district && formData.districtStatus === "available";
      case 4:
        return formData.pincode && formData.pincodeStatus === "available";
      default:
        return false;
    }
  };

  // Check if the current step's selection is restricted
  const isCurrentStepRestricted = () => {
    switch (currentStep) {
      case 2:
        return restrictedLocations.includes(formData.state);
      case 3:
        return restrictedLocations.includes(
          formData.district?.replace(": Metro", ""),
        );
      case 4:
        return restrictedLocations.includes(formData.pincode);
      default:
        return false;
    }
  };

  // Check if the current step is loading
  const isCurrentStepLoading = () => {
    switch (currentStep) {
      case 0:
        return loading.categories;
      case 1:
        return loading.countries;
      case 2:
        return loading.states;
      case 3:
        return loading.districts;
      case 4:
        return loading.pincodes;
      default:
        return false;
    }
  };

  // Function to generate dynamic heading based on step and formData
  const getDynamicHeading = () => {
    switch (currentStep) {
      case 0:
        return "LifeOnPlus Product Portfolio – Channel Partner Distribution";
      case 1:
        return formData.country
          ? `Country Partner Login – ${formData.country}`
          : "Country Partner Login";
      case 2:
        return formData.state && formData.country
          ? `State Selection – ${formData.state} (${formData.country})`
          : formData.country
            ? `State Selection – ${formData.country}`
            : "State Selection";
      case 3:
        return formData.district && formData.state
          ? `District Selection – ${formData.district}, ${formData.state}`
          : formData.state
            ? `District Selection – ${formData.state}`
            : "District Selection";
      case 4:
        return formData.district && formData.state
          ? `Pincode and Retailer Selection – ${formData.pincode}, ${formData.district}, ${formData.state}`
          : formData.state
            ? `Pincode and Retailer Selection – ${formData.state}`
            : "Pincode and Retailer Selection";
      default:
        return "Channel Partner Registration";
    }
  };

  // Check if a step is completed
  const isStepCompleted = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return formData.category !== null;
      case 1:
        return formData.country !== null;
      case 2:
        return formData.state !== null;
      case 3:
        return formData.district !== null;
      case 4:
        return formData.pincode !== null;
      default:
        return false;
    }
  };

  // Get partner level based on current step
  const getLevel = () => {
    if (isPlusMode) return "partnerPlus"; // or whatever your backend expects
    switch (currentStep) {
      case 1:
        return "country-partner";
      case 2:
        return "state-partner";
      case 3:
        return "district-partner";
      case 4:
        return applicationType || "pincode-partner";
      default:
        return "";
    }
  };

  // Form validation schema using Yup
  const validationSchema = [
    // Step 0: Personal Details and Address
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      // ── Conditional fields for normal mode only ──
      ...(!isPlusMode
        ? {
            partnerApplyAddress: Yup.string().required(
              "Partner Address is required",
            ),
          }
        : {}),
      resAddress_line: Yup.string().required("Residential Address is required"),
      resCountry: Yup.string().required("Residential Country is required"),
      resState: Yup.string().required("Residential State is required"),
      resCity: Yup.string().required("Residential City is required"),
      resDistrict: Yup.string().required("Residential District is required"),
      resPincode: Yup.string().required("Residential Pincode is required"),
      correAddress_line: Yup.string().required(
        "Correspondence/Office Address is required",
      ),
      correCountry: Yup.string().required(
        "Correspondence/Office Country is required",
      ),
      correState: Yup.string().required(
        "Correspondence/Office State is required",
      ),
      correCity: Yup.string().required(
        "Correspondence/Office City is required",
      ),
      correDistrict: Yup.string().required(
        "Correspondence/Office District is required",
      ),
      correPincode: Yup.string().required(
        "Correspondence/Office Pincode is required",
      ),
      dob: Yup.date().required("Date of birth is required"),
      gender: Yup.string().required("Gender is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
      businessName: Yup.string(),
      gstNo: Yup.string(),
    }),
    // Step 1: Bank Details and KYC
    Yup.object().shape({
      bankDetails: Yup.object().shape({
        holderName: Yup.string().notRequired().trim().nullable(),
        bankName: Yup.string().notRequired().trim().nullable(),
        accountNo: Yup.string()
          .notRequired()
          .trim()
          .nullable()
          .matches(/^\d{9,18}$/, {
            message: "Account number must be 9-18 digits",
            excludeEmptyString: true, // ← important: don't validate empty string
          }),
        ifscCode: Yup.string()
          .notRequired()
          .trim()
          .nullable()
          .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, {
            message: "Invalid IFSC code format",
            excludeEmptyString: true,
          }),
      }),
      aadharCard: Yup.mixed()
        .nullable()
        .test(
          "fileType",
          "Aadhar card must be a PDF, JPG, JPEG, or PNG",
          (value) => {
            if (!value) return true; // allow empty
            return ["application/pdf", "image/jpeg", "image/png"].includes(
              value.type,
            );
          },
        ),
      panCard: Yup.mixed()
        .required("PAN card is required")
        .test(
          "fileType",
          "PAN card must be a PDF, JPG, JPEG, or PNG",
          (value) =>
            value &&
            ["application/pdf", "image/jpeg", "image/png"].includes(value.type),
        ),
      aadharCardNo: Yup.string().matches(
        /^\d{12}$/,
        "Aadhar card number must be 12 digits",
      ),
      panCardNo: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN card number format")
        .required("PAN card number is required"),
    }),
    // Step 2: Referral Code
    Yup.object().shape({
      referralCode: Yup.string(),
    }),
    // Step 3: Transaction Details
    Yup.object().shape({
      // transactionId: Yup.string().required("Transaction ID is required"),
      // transferType: Yup.string().required("Transfer type is required"),
      amount: Yup.number()
        .required("Total amount is required")
        .positive("Total amount must be positive"),
      grossAmount: Yup.number()
        .required("Base amount is required")
        .positive("Base amount must be positive"),
      gstAddedAmount: Yup.number()
        .required("GST amount is required")
        .positive("GST amount must be positive"),
      // screenshot: Yup.mixed().required("Payment screenshot is required"),
    }),
    // Step 4: Terms and Conditions
    Yup.object().shape({
      agreement: Yup.boolean()
        .required("You must agree to the Terms and Conditions")
        .oneOf([true], "You must agree to the Terms and Conditions"),
    }),
  ];

  // Toggle form modal visibility
  const handleShowFormModal = (type) => {
    if (
      type === "retailer" ||
      (isCurrentStepAvailable() &&
        !isCurrentStepLoading() &&
        !isCurrentStepRestricted())
    ) {
      setApplicationType(type);
      setShowFormModal(true);
    }
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setApplicationType(null);
    navigate("/");
  };

  // Toggle thank you message
  const handleCloseThankYou = () => {
    setShowThankYou(false);
    setShowFormModal(false);
    setCurrentStep(0);
    setApplicationType(null);
    setFormData({
      category: null,
      country: null,
      countryStatus: null,
      state: null,
      stateStatus: null,
      district: null,
      districtStatus: null,
      isMetro: false,
      pincode: null,
      pincodeStatus: null,
    });
    setFilterType("all");
    setSearchQueryState("");
    setSearchQueryDistrict("");
    setSearchQueryPincode("");
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // Update form data with status
  const handleSelect = (key, value, status = null, isMetro = false) => {
    setFormData((prevData) => {
      if (key === "category" && prevData.category !== value) {
        setOptions({
          countries: [],
          states: [],
          unionTerritories: [],
          districts: [],
          pincodes: [],
        });
        return {
          category: value,
          country: null,
          countryStatus: null,
          state: null,
          stateStatus: null,
          district: null,
          districtStatus: null,
          isMetro: false,
          pincode: null,
          pincodeStatus: null,
        };
      }
      let resetFields = {};
      if (key === "country") {
        resetFields = {
          state: null,
          stateStatus: null,
          district: null,
          districtStatus: null,
          isMetro: false,
          pincode: null,
          pincodeStatus: null,
        };
        setOptions((prev) => ({
          ...prev,
          states: [],
          unionTerritories: [],
          districts: [],
          pincodes: [],
        }));
      } else if (key === "state") {
        resetFields = {
          district: null,
          districtStatus: null,
          isMetro: false,
          pincode: null,
          pincodeStatus: null,
        };
        setOptions((prev) => ({ ...prev, districts: [], pincodes: [] }));
      } else if (key === "district") {
        resetFields = {
          pincode: null,
          pincodeStatus: null,
        };
        setOptions((prev) => ({ ...prev, pincodes: [] }));
      }
      return {
        ...prevData,
        [key]: value,
        ...(status ? { [`${key}Status`]: status } : {}),
        ...(key === "district" ? { isMetro } : {}),
        ...resetFields,
      };
    });
  };

  // Fetch categories
  const fetchVerticalProducts = async () => {
    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const response = await fetch(
        `${base_url}v1/vertical/getAllProdcts?page=1&limit=100`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setCategories(data?.mainProducts || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  // Fetch countries based on category
  useEffect(() => {
    if (formData.category) {
      setLoading((prev) => ({ ...prev, countries: true }));
      axios
        .post(
          `${base_url}v1/user/country-availability?mainProductId=${formData.category}&partnerType=${partnerType}`,
        )
        .then((response) => {
          const countriesData =
            response.data.data?.filter((e) => e.name === "India") || [];
          setOptions((prev) => ({ ...prev, countries: countriesData }));
        })
        .catch((error) => {
          console.error("Error fetching countries:", error);
          setOptions((prev) => ({ ...prev, countries: [] }));
        })
        .finally(() => {
          setLoading((prev) => ({ ...prev, countries: false }));
        });
    }
  }, [formData.category]);

  // Fetch states and union territories based on country and search
  useEffect(() => {
    if (formData.country === "India") {
      setLoading((prev) => ({ ...prev, states: true }));
      axios
        .post(
          `${base_url}v1/user/state-availability?mainProductId=${
            formData.category
          }&partnerType=${partnerType}&country=India&search=${encodeURIComponent(
            searchQueryState,
          )}`,
        )
        .then((response) => {
          const data = response.data || [];
          // Separate states and union territories using the static list
          const states = data.filter(
            (item) => !unionTerritoriesList.includes(item.name),
          );
          const unionTerritories = data.filter((item) =>
            unionTerritoriesList.includes(item.name),
          );
          setOptions((prev) => ({ ...prev, states, unionTerritories }));
        })
        .catch((error) => {
          console.error("Error fetching states:", error);
          setOptions((prev) => ({
            ...prev,
            states: [],
            unionTerritories: [],
            districts: [],
            pincodes: [],
          }));
        })
        .finally(() => {
          setLoading((prev) => ({ ...prev, states: false }));
        });
    } else if (formData.country === "International") {
      setOptions((prev) => ({
        ...prev,
        states: [{ name: "N/A", status: "available" }],
        unionTerritories: [],
        districts: [],
        pincodes: [],
      }));
      setFormData((prev) => ({
        ...prev,
        state: null,
        stateStatus: null,
        district: null,
        districtStatus: null,
        isMetro: false,
        pincode: null,
        pincodeStatus: null,
      }));
    } else {
      setOptions((prev) => ({
        ...prev,
        states: [],
        unionTerritories: [],
        districts: [],
        pincodes: [],
      }));
      setFormData((prev) => ({
        ...prev,
        state: null,
        stateStatus: null,
        district: null,
        districtStatus: null,
        isMetro: false,
        pincode: null,
        pincodeStatus: null,
      }));
    }
  }, [formData.country, formData.category, searchQueryState]);

  // Fetch districts based on state and search
  useEffect(() => {
    if (formData.state && formData.state !== "N/A") {
      setLoading((prev) => ({ ...prev, districts: true }));
      axios
        .post(
          `${base_url}v1/user/district-availability?mainProductId=${
            formData.category
          }&partnerType=${partnerType}&country=India&state=${
            formData.state
          }&search=${encodeURIComponent(searchQueryDistrict)}`,
        )
        .then((response) => {
          const districts = response.data || [];
          setOptions((prev) => ({ ...prev, districts }));
        })
        .catch((error) => {
          console.error("Error fetching districts:", error);
          setOptions((prev) => ({ ...prev, districts: [], pincodes: [] }));
          setFormData((prev) => ({
            ...prev,
            district: null,
            districtStatus: null,
            isMetro: false,
            pincode: null,
            pincodeStatus: null,
          }));
        })
        .finally(() => {
          setLoading((prev) => ({ ...prev, districts: false }));
        });
    } else {
      setOptions((prev) => ({ ...prev, districts: [], pincodes: [] }));
      setFormData((prev) => ({
        ...prev,
        district: null,
        districtStatus: null,
        isMetro: false,
        pincode: null,
        pincodeStatus: null,
      }));
    }
  }, [formData.state, formData.category, searchQueryDistrict]);

  // Fetch pincodes based on district and search
  useEffect(() => {
    if (formData.district) {
      setLoading((prev) => ({ ...prev, pincodes: true }));
      const districtName = formData.district.replace(": Metro", "");
      axios
        .post(
          `${base_url}v1/user/pincode-availability?mainProductId=${
            formData.category
          }&partnerType=${partnerType}&country=India&state=${
            formData.state
          }&district=${districtName}&search=${encodeURIComponent(
            searchQueryPincode,
          )}`,
        )
        .then((response) => {
          const pincodes = response.data || [];
          setOptions((prev) => ({ ...prev, pincodes }));
        })
        .catch((error) => {
          console.error("Error fetching pincodes:", error);
          setOptions((prev) => ({ ...prev, pincodes: [] }));
          setFormData((prev) => ({
            ...prev,
            pincode: null,
            pincodeStatus: null,
          }));
        })
        .finally(() => {
          setLoading((prev) => ({ ...prev, pincodes: false }));
        });
    } else {
      setOptions((prev) => ({ ...prev, pincodes: [] }));
      setFormData((prev) => ({ ...prev, pincode: null, pincodeStatus: null }));
    }
  }, [
    formData.district,
    formData.category,
    formData.state,
    searchQueryPincode,
  ]);

  const fetchPaymentData = async () => {
    try {
      const response = await axios.get(`${base_url}v1/user/partnerPaymentData`);
      setPaymentData(
        window.location.pathname === "/signup/channelpartnerplus"
          ? response.data.data.partnerPlusPayment
          : response.data.data.partnerPayment,
      );
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  useEffect(() => {
    fetchPaymentData();
    fetchVerticalProducts();
  }, []);

  const steps = [
    "LifeOnPlus Product Portfolio – Channel Partner Distribution",
    "Register as Country Partner",
    "Register as State Partner",
    "Register as District Partner",
    "Register as Pincode Partner",
  ];

  const steps1 = ["Region", "State Or UT", "District", "Pincode"];
  // Toggle description for a specific product
  const toggleDescription = (productId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  // Get short description (truncate to 100 characters)
  const getShortDescription = (description) => {
    if (!description) return "No description available.";
    return description.length > 50
      ? description.slice(0, 50) + "..."
      : description;
  };

  const SelectionCard = ({
    index, // <-- pass index when rendering in map
    region,
    item,
    isSelected,
    registeredBy,
    status,
    img,
    onClick,
    type = "card",
    isMetro = false,
    isDisabled = false,
    description,
    productId,
  }) => {
    let tooltipMessage = "";

    if (region === "Country") {
      tooltipMessage = `Click on <span style="color: #FFA500; ">Proceed to register as Country Partner</span>. Or, if you want to register as State/UT Partner, click on the Country and then click <span style="color: #FF8C00; ">Proceed to select your State/UT</span>.`;
    } else if (region === "State") {
      tooltipMessage = `Click on <span style="color: #FFA500; ">Proceed to register as State Partner</span>. Or, if you want to register as District Partner, click on the State/UT and then click <span style="color: #FF8C00; ">Proceed to select your District</span>.`;
    } else if (region === "District") {
      tooltipMessage = `Click on <span style="color: #FFA500; ">Proceed to register as District Partner</span>. Or, if you want to register as Pincode Partner, click on the District and then click <span style="color: #FF8C00; ">Proceed to select your Pincode</span>.`;
    } else if (region === "Pincode") {
      tooltipMessage = `Click on <span style="color: #FFA500; ">Proceed to register as Pincode Partner</span>. Or, if you want to register as Retailer, click on <span style="color: #FFA500; ">Proceed to register as Retailer</span>.`;
    }

    const displayName =
      isMetro && type === "list"
        ? `${item.replace(": Metro", "")} (Metro)`
        : item;

    const isRestricted = restrictedLocations.includes(
      item.replace(": Metro", ""),
    );

    // Only toggle tooltip if the card is selected
    const handleMouseEnter = () => {
      if (isSelected) {
        setIsSelectedTip(true);
      }
    };

    const handleMouseLeave = () => {
      setIsSelectedTip(false);
    };

    // ✅ Decide tooltip side based on index (4 cards per row)
    const isRightSide = index % 4 === 0 || index % 4 === 1;
    return (
      <div
        onClick={isDisabled ? null : () => onClick(item, status, isMetro)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative cursor-pointer border-gray-500 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 ${
          isDisabled
            ? "bg-gray-300 cursor-not-allowed"
            : isRestricted
              ? "bg-[#FFA500]"
              : status === "available"
                ? "bg-green-400"
                : "bg-red-600"
        } ${isSelected ? "ring-2 ring-black" : ""} ${
          type === "card"
            ? "bg-white p-2"
            : "flex items-center h-14 px-4 w-32 sm:w-40"
        }`}
      >
        {/* Tooltip (show when selected) */}
        {isSelected && isSelectedTip && type == "list" && (
          <div
            className={`absolute w-80 top-1/2 -translate-y-1/2 bg-black text-white text-xs font-medium px-3 py-2 rounded-lg shadow-md z-20 max-w-xs whitespace-normal ${
              isRightSide
                ? "left-full ml-2 text-left"
                : "right-full mr-2 text-left"
            }`}
          >
            <div dangerouslySetInnerHTML={{ __html: tooltipMessage }} />
            {/* Arrow */}
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-black rotate-45 ${
                isRightSide ? "-left-1.5" : "-right-1.5"
              }`}
            ></div>
          </div>
        )}

        {type === "card" ? (
          <div className="flex flex-col items-center">
            <div
              className="w-full h-36 bg-gray-200 rounded-t-lg bg-cover bg-no-repeat"
              style={{ backgroundImage: `url(${img})` }}
            ></div>
            <span className="text-md font-['Roboto'] text-center text-black py-2">
              {displayName}
            </span>
            {description && (
              <div className="text-xs font-['Poppins'] text-gray-600 px-2 pb-2 text-center">
                {expandedDescriptions[productId] ? (
                  <>
                    {description}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDescription(productId);
                      }}
                      className="text-blue-600 hover:underline ml-1 text-xs"
                    >
                      Read Less
                    </button>
                  </>
                ) : (
                  <>
                    {getShortDescription(description)}
                    {description.length > 50 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDescription(productId);
                        }}
                        className="text-blue-600 hover:underline ml-1 text-xs"
                      >
                        Read More
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-sm font-['Roboto'] text-black truncate">
            {displayName}
          </span>
        )}

        {isDisabled && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg pointer-events-none">
            <span className="text-white text-sm font-['Poppins'] font-semibold">
              Coming Soon
            </span>
          </div>
        )}
      </div>
    );
  };

  const subheadings = [
    "Select a product category to begin your partner registration.",
    "Confirm your Country and continue to complete your Country Partner registration form.",
    "Confirm your State and continue to complete your State Partner registration form.",
    "Confirm your District and continue to complete your District Partner registration form.",
    "Confirm your Pincode and continue to complete your Pincode Partner registration form.",
  ];
  const instructions = [
    "LifeOnPlus presents a unique opportunity to be part of a global healthcare distribution ecosystem by selecting the product(s) you wish to represent and leading sales, marketing, and distribution within your chosen territory. Our portfolio covers the below spectrum of innovative health solutions.",
    "Select your country and continue if you wish to register as a Country Partner. If not, you can still proceed to register as State or District Partner by click on Proceed to Select State Or UT.",
    "First, select your country. Then choose your State or Union Territory to continue State Partner registration.",
    "Select your Country and State to proceed with District Partner registration.",
    "Select your Country, State, and District to proceed with Pincode Partner registration.",
  ];

  // Pop-up messages for validation (shown on hover)
  const validationMessages = {
    1: "⚠️ Please select your Country first to continue with State Partner registration.",
    2: "⚠️ Please select your State or Union Territory first to continue with District Partner registration.",
    3: "⚠️ Please select your District first to continue with Pincode Partner registration.",
    4: "⚠️ Please select your Pincode first to continue with Retailer registration.",
  };

  // Determine if the button should be disabled and show tooltip
  const getButtonDisabledState = (field) => {
    const fields = ["category", "country", "state", "district"];
    return !formData[fields[currentStep]] || isCurrentStepLoading();
  };

  // Filter logic for step 2 (client-side filter for type after server-side search)
  const getFilteredItems = () => {
    const allItems = [...options.states, ...options.unionTerritories];
    let filteredItems = allItems;
    // Apply filter based on filterType
    if (filterType === "states") {
      filteredItems = options.states;
    } else if (filterType === "unionTerritories") {
      filteredItems = options.unionTerritories;
    }
    return filteredItems;
  };

  const formatIndianNumber = (num) => {
    return num.toLocaleString("en-IN"); // e.g. 18000000 => 1,80,00,000
  };

  const formatAmount = (label, amount) => {
    if (!amount) return `${label}: N/A`;
    return `${label}: ₹${formatIndianNumber(amount)}`;
  };

  const getAmount = (step) => {
    if (!paymentData) return "Loading...";

    const config = LEVEL_MAP[step];
    if (!config) return "";

    // STEP 1 & 2 (no metro concept in array)
    if (step === 1 || step === 2) {
      return formatSingleOrRange(
        config.label,
        getAmounts({
          paymentData,
          partnerType,
          levelKey:
            partnerType === "channelPartner"
              ? config.objectKey
              : config.arrayLevel,
        }),
      );
    }

    // STEP 3 (District)
    if (step === 3) {
      // channelPartner → metro / non-metro
      if (partnerType === "channelPartner") {
        return [
          formatSingleOrRange(
            "District (Metro)",
            getAmounts({
              paymentData,
              partnerType,
              levelKey: config.objectKey.metro,
            }),
          ),
          formatSingleOrRange(
            "District (Non-Metro)",
            getAmounts({
              paymentData,
              partnerType,
              levelKey: config.objectKey.nonMetro,
            }),
          ),
        ].join(" | ");
      }

      // channelPartnerPlus → single range
      return formatSingleOrRange(
        "District",
        getAmounts({
          paymentData,
          partnerType,
          levelKey: config.arrayLevel,
        }),
      );
    }

    // STEP 4 (Pincode + Retailer)
    if (step === 4) {
      // channelPartner
      if (partnerType === "channelPartner") {
        return [
          formatSingleOrRange(
            formData.isMetro ? "Pincode (Metro)" : "Pincode (Non-Metro)",
            getAmounts({
              paymentData,
              partnerType,
              levelKey: formData.isMetro
                ? config.objectKey.metro
                : config.objectKey.nonMetro,
            }),
          ),
          formatSingleOrRange(
            formData.isMetro ? "Retailer (Metro)" : "Retailer (Non-Metro)",
            getAmounts({
              paymentData,
              partnerType,
              levelKey: formData.isMetro
                ? config.retailer.objectKey.metro
                : config.retailer.objectKey.nonMetro,
            }),
          ),
        ].join(" | ");
      }

      // channelPartnerPlus → no metro logic
      return [
        formatSingleOrRange(
          "Pincode",
          getAmounts({
            paymentData,
            partnerType,
            levelKey: config.arrayLevel,
          }),
        ),
        formatSingleOrRange(
          "Retailer",
          getAmounts({
            paymentData,
            partnerType,
            levelKey: config.retailer.arrayLevel,
          }),
        ),
      ].join(" | ");
    }

    return "";
  };

  const formatSingleOrRange = (label, amounts) => {
    console.log(amounts, partnerType, window.location.pathname);
    if (!amounts.length) return `${label}: N/A`;

    if (amounts.length === 1) {
      return formatAmount(label, amounts[0]);
    }

    const min = Math.min(...amounts);
    const max = Math.max(...amounts);

    return `: ₹${min.toLocaleString()} - ₹${max.toLocaleString()}`;
  };

  const getAmounts = ({ paymentData, partnerType, levelKey }) => {
    if (!paymentData) return [];

    // channelPartner → object
    if (partnerType === "channelPartner") {
      const value = paymentData[levelKey];
      return value !== undefined ? [value] : [];
    }

    // channelPartnerPlus → array
    return paymentData
      .filter((item) => item.level === levelKey)
      .map((item) => item.amount);
  };

  const LEVEL_MAP = {
    1: {
      label: "Country",
      objectKey: "country",
      arrayLevel: "country-partner",
    },
    2: {
      label: "State",
      objectKey: "state",
      arrayLevel: "state-partner",
    },
    3: {
      label: "District",
      objectKey: {
        metro: "districtMetro",
        nonMetro: "districtNonMetro",
      },
      arrayLevel: "district-partner",
    },
    4: {
      label: "Pincode",
      objectKey: {
        metro: "pincodeMetro",
        nonMetro: "pincodeNonMetro",
      },
      arrayLevel: "pincode-partner",
      retailer: {
        objectKey: {
          metro: "retailerMetro",
          nonMetro: "retailerNonMetro",
        },
        arrayLevel: "retailer",
      },
    },
  };

  // Reusable step component with scrollable content and loading state
  const StepContent = ({ step }) => (
    <div className="flex flex-col justify-center min-h-[500px] sm:min-h-[700px] p-4 sm:p-6">
      <div className="sticky top-0 z-30 bg-white shadow-md py-4 px-4 sm:px-6">
        <h1 className="text-lg sm:text-lg font-['Poppins'] font-bold text-gray-900">
          {getDynamicHeading()}
        </h1>
      </div>
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-['Poppins'] font-semibold text-gray-800 mb-2">
          {steps[step]}
        </h2>
        <p className="text-lg font-['Poppins'] text-gray-600 mb-2 font-semibold">
          Steps to register as channel partner
        </p>
        <p className="text-[14px] font-['Poppins'] text-gray-600 font-semibold">
          {instructions[step]}
        </p>
        <p className="text-[13px] font-['Poppins'] mt-3 text-gray-400 font-semibold">
          {subheadings[step]}
        </p>
        <div>
          {step > 0 && (
            <div className="flex items-center gap-2 text-xs font-['Poppins']">
              <span className="flex items-center">
                <span className="w-3 h-3 bg-green-400 rounded-full mr-1"></span>{" "}
                Active/Available
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 bg-red-600 rounded-full mr-1"></span>{" "}
                Allotted/Unavailable
              </span>
              <span className="flex items-center">
                <span className="w-3 h-3 bg-[#FFA500] rounded-full mr-1"></span>{" "}
                In Process/Pending
              </span>
            </div>
          )}
        </div>
        <div>
          {step > 0 && (
            <p className="text-[13px] font-['Poppins'] mt-2 text-gray-700 font-semibold">
              Amount {getAmount(step)}
            </p>
          )}
        </div>
      </div>
      {step == 0 && (
        <button
          onClick={() => setCurrentStep(currentStep + 1)}
          disabled={getButtonDisabledState()}
          className="w-[300px] px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
        >
          Proceed to Select {steps1[currentStep]}
        </button>
      )}

      {isCurrentStepLoading() ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
        </div>
      ) : (
        <>
          {step === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto p-2.5">
              {categories.map((category, index) => (
                <SelectionCard
                  key={category._id}
                  item={category.mainProductName}
                  img={category.images[0]}
                  isSelected={formData.category === category._id}
                  onClick={() => handleSelect("category", category._id)}
                  isDisabled={![0, 1, 2, 3, 4].includes(index)}
                  description={category.description}
                  productId={category._id}
                />
              ))}
            </div>
          )}
          {step === 1 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-h-[400px] overflow-y-auto p-2.5">
              {options.countries.map((country, index) => (
                <SelectionCard
                  index={index}
                  region={"Country"}
                  key={country.name}
                  item={country.name}
                  status={country.status}
                  isSelected={formData.country === country.name}
                  registeredBy={country.registeredBy}
                  onClick={() =>
                    handleSelect("country", country.name, country.status)
                  }
                  type="list"
                />
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="max-h-[400px] overflow-y-auto p-2.5">
              <div className="mb-4 flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search states or union territories..."
                  value={searchQueryState}
                  onChange={(e) => setSearchQueryState(e.target.value)}
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="p-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                >
                  <option value="all">All</option>
                  <option value="states">States</option>
                  <option value="unionTerritories">Union Territories</option>
                </select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {getFilteredItems().map((item, index) => (
                  <SelectionCard
                    index={index}
                    region={"State"}
                    key={item.name}
                    item={item.name}
                    status={item.status}
                    isSelected={formData.state === item.name}
                    registeredBy={item.registeredBy}
                    onClick={() =>
                      handleSelect("state", item.name, item.status)
                    }
                    type="list"
                  />
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="max-h-[400px] overflow-y-auto p-2.5">
              <div className="mb-4 flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search districts..."
                  value={searchQueryDistrict}
                  onChange={(e) => setSearchQueryDistrict(e.target.value)}
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {options.districts.map((district, index) => {
                  const isMetro = district.name.includes(": Metro");
                  return (
                    <SelectionCard
                      index={index}
                      region={"District"}
                      key={district.name}
                      item={district.name}
                      status={district.status}
                      isSelected={formData.district === district.name}
                      registeredBy={district.registeredBy}
                      onClick={() =>
                        handleSelect(
                          "district",
                          district.name,
                          district.status,
                          isMetro,
                        )
                      }
                      type="list"
                      isMetro={isMetro}
                    />
                  );
                })}
              </div>
            </div>
          )}
          {step === 4 && (
            <div className="max-h-[400px] overflow-y-auto p-2.5">
              <div className="mb-4 flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Search pincodes..."
                  value={searchQueryPincode}
                  onChange={(e) => setSearchQueryPincode(e.target.value)}
                  className="w-full p-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {options.pincodes.map((pincode, index) => (
                  <SelectionCard
                    index={index}
                    region={"Pincode"}
                    key={pincode.name}
                    item={pincode.name}
                    status={pincode.status}
                    isSelected={formData.pincode === pincode.name}
                    registeredBy={pincode.registeredBy}
                    onClick={() =>
                      handleSelect("pincode", pincode.name, pincode.status)
                    }
                    type="list"
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      <div className="mt-8 flex justify-between space-x-2">
        {currentStep > 0 && (
          <button
            onClick={handlePrevious}
            disabled={isCurrentStepLoading()}
            className="px-4 py-2 bg-[#1DA1F2]  text-white font-['Poppins'] rounded-lg  transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
          >
            Back
          </button>
        )}
        {currentStep === 4 ? (
          <div className="flex space-x-2">
            <button
              onClick={() => handleShowFormModal("pincode-partner")}
              disabled={
                !isCurrentStepAvailable() ||
                isCurrentStepLoading() ||
                isCurrentStepRestricted()
              }
              className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg  transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
            >
              Register as Pincode Partner
            </button>
            <button
              onClick={() => handleShowFormModal("retailer")}
              disabled={!formData.pincode || isCurrentStepLoading()}
              className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
            >
              Register as Retailer Partner
            </button>
          </div>
        ) : (
          currentStep > 0 && (
            <button
              onClick={() => handleShowFormModal(getLevel())}
              disabled={
                !isCurrentStepAvailable() ||
                isCurrentStepLoading() ||
                isCurrentStepRestricted()
              }
              className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
            >
              Proceed to {steps[currentStep]}
            </button>
          )
        )}
        {currentStep < steps.length - 1 && (
          <div className="relative">
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={getButtonDisabledState()}
              className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
            >
              Proceed to Select {steps1[currentStep]}
            </button>
            {!formData[Object.keys(formData)[currentStep * 2]] && (
              <div className="absolute hidden group-hover:block bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-2 px-3 w-64 text-center shadow-lg z-10">
                {validationMessages[currentStep]}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-700 space-y-2 my-3">
        <h3 className="text-sm font-bold text-gray-900">
          Channel Partner Hierarchy
        </h3>
        <p>
          LifeOnPlus has a structured distribution hierarchy to ensure organized
          and fair growth:
        </p>
        <p>
          <span className="font-medium">1. Country Partner</span> – Exclusive
          leadership for an entire country; oversees state-level partners.
        </p>
        <p>
          <span className="font-medium">2. State Partner</span> – Leads
          operations within a state/union territory; appoints district partners.
        </p>
        <p>
          <span className="font-medium">3. District Partner</span> – Handles
          distribution and expansion across the district; manages pin code
          partners.
        </p>
        <p>
          <span className="font-medium">4. Pin Code Partner</span> – Focuses on
          hyper-local distribution within a specific pin code.
        </p>
        <p>
          <span className="font-medium">
            5. Retailer / Point-of-Sale Partner
          </span>{" "}
          – Directly engages with end customers, driving last-mile adoption.
        </p>
        <h3 className="text-sm font-bold text-gray-900">
          Partner Responsibilities
        </h3>
        <p>• Select product(s) from the LifeOnPlus portfolio.</p>
        <p>
          • Take leadership for distribution, marketing, and sales within the
          assigned region.
        </p>
        <p>
          • Build and manage a local partner/retail network as per hierarchy.
        </p>
        <p>
          • Ensure compliance with LifeOnPlus pricing, branding, and operational
          guidelines.
        </p>
        <p>
          • Drive awareness, adoption, and revenue growth within the allocated
          territory.
        </p>
        <h3 className="font-bold text-sm">To Enrol</h3> Follow the Steps to
        Register as a Channel Partner, to start your jouney with Lifeonplus!
      </div>
    </div>
  );

  useEffect(() => {
    const completeSignup = async () => {
      const formData = JSON.parse(localStorage.getItem("signupFormData"));
      const orderId = localStorage.getItem("cashfreeOrderId");
      const customerId = localStorage.getItem("cashfreeCustomerId");

      if (!orderId || !formData) return;

      const finalFormData = new FormData();

      // append ALL signup fields again
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          finalFormData.append(key, formData[key]);
        }
      });

      // append orderId (IMPORTANT)
      finalFormData.append("orderId", orderId);
      finalFormData.append("customerId", customerId);

      // await axios.post(`${base_url}v1/user/userPartner/signUp`, finalFormData, {
      //   headers: { "Content-Type": "multipart/form-data" },
      // });

      localStorage.removeItem("signupFormData");
      localStorage.removeItem("cashfreeOrderId");
    };

    completeSignup();
  }, []);

  // Application Form Modal
  const ApplicationFormModal = () => {
    const partnerType =
      window.location.pathname === "/signup/channelpartnerplus"
        ? "channelPartnerPlus"
        : "channelPartner";
    const formEnabled =
      partnerType === "channelPartnerPlus" || applicationType === "retailer"
        ? true
        : isCurrentStepAvailable() && !isCurrentStepRestricted();
    const [step, setStep] = useState(0);
    const [paymentData, setPaymentData] = useState(null);
    const [isRegistered, setIsRegistered] = useState(false);
    const [referralData, setReferralData] = useState(null);
    const [referralCodeInput, setReferralCodeInput] = useState("");
    const [selectedGrossAmount, setSelectedGrossAmount] = useState("");
    const [useSameAddress, setUseSameAddress] = useState(false);
    const [showIntro, setShowIntro] = useState(
      partnerType === "channelPartnerPlus",
    );
    const [couponCode, setCouponCode] = useState("");
    const [discount, setDiscount] = useState({
      type: null,
      value: 0,
      id: null,
    });
    const [couponError, setCouponError] = useState("");
    const [couponDetails, setCouponDetails] = useState(null);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

    const handleRemoveCoupon = () => {
      setCouponCode("");
      setDiscount({ type: null, value: 0, id: null });
      setCouponError("");
      setCouponDetails(null);
      toast.success("Coupon removed successfully!");
    };

    const handleApplyCoupon = async () => {
      if (!couponCode) {
        setCouponError("Please enter a coupon code.");
        return;
      }

      setIsApplyingCoupon(true);
      try {
        const response = await fetch(
          `${base_url}coupon/getCoupon/${couponCode}?type=channel_partner_signup`,
        );

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.message || "Invalid coupon code.");
        }

        const coupon = result.data;
        const currentDate = new Date();
        const startDate = new Date(coupon.startDate);
        const expirationDate = new Date(coupon.expirationDate);

        if (coupon.status !== "active") {
          throw new Error("Coupon is not active.");
        }
        if (currentDate < startDate || currentDate > expirationDate) {
          throw new Error("Coupon is expired or not yet valid.");
        }
        const amounts = getAmountDetails();
        if (
          coupon.discountType === "flat" &&
          coupon.discountValue > amounts.grossAmount
        ) {
          throw new Error("Invalid Coupon");
        }

        setDiscount({
          type: coupon.discountType,
          value: coupon.discountValue,
          id: coupon._id,
        });
        setCouponDetails({
          title: coupon.title,
          description: coupon.description,
          couponCode: coupon.couponCode,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue,
        });
        setCouponError("");
        toast.success("Coupon applied successfully!");
      } catch (error) {
        setCouponError(error.message);
        setDiscount({ type: null, value: 0, id: null });
        setCouponDetails(null);
        toast.error(error.message);
      } finally {
        setIsApplyingCoupon(false);
      }
    };

    const formSteps = [
      "Personal Details",
      "Bank Details & KYC",
      "Referral Code",
      "Transaction Details",
      "Terms and Conditions",
    ];
    const [isInstallment, setIsInstallment] = useState(false);
    const [payingAmount, setPayingAmount] = useState("");
    const formatIndianNumber = (num) => {
      return num.toLocaleString("en-IN");
    };

    const calculateAmountDetails = (grossAmount, payingAmount = 0) => {
      if (!grossAmount || isNaN(grossAmount)) return null;

      const gstRate = 0.18;

      /* -------------------------------- */
      /* CHANNEL PARTNER PLUS (NO DISCOUNT) */
      /* -------------------------------- */

      if (partnerType === "channelPartnerPlus") {
        const totalAmount = Math.round(grossAmount);

        const regFee = +(totalAmount / (1 + gstRate)).toFixed(2);
        const gstAddedAmount = +(totalAmount - regFee).toFixed(2);

        const paidAmount = payingAmount || totalAmount;
        const remainingAmount = totalAmount - paidAmount;

        return {
          grossAmount: regFee,
          baseAmount: regFee,
          regFee,
          gstAddedAmount,
          totalAmount,

          payingAmount: paidAmount,
          remainingAmount,

          paidRegFee: Math.min(regFee, paidAmount),
          paidGST: Math.max(0, paidAmount - regFee),

          stock: 0,
          paidStock: 0,
        };
      }

      /* -------------------------------- */
      /* CHANNEL PARTNER (WITH DISCOUNT) */
      /* -------------------------------- */

      let finalTotal = grossAmount;

      // ✅ Apply discount ONLY here
      if (discount && discount.value > 0) {
        if (discount.type === "percentage") {
          finalTotal = grossAmount - (grossAmount * discount.value) / 100;
        } else if (discount.type === "flat") {
          finalTotal = grossAmount - discount.value;
        }
      }
      console.log(finalTotal, Math.round(finalTotal));

      finalTotal = Math.round(finalTotal);

      // ✅ Convert TOTAL → BASE
      let stock = finalTotal * 0.7;
      // 30% platform fee INCLUDING GST
      let platformFeeWithGST = finalTotal * 0.3;

      // Extract GST from platform fee
      let regFee = platformFeeWithGST / (1 + gstRate);
      let gstAddedAmount = platformFeeWithGST - regFee;

      stock = Number(stock.toFixed(2));
      regFee = Number(regFee.toFixed(2));
      gstAddedAmount = Number(gstAddedAmount.toFixed(2));

      const totalAmount = Math.ceil(
        (stock + regFee + gstAddedAmount).toFixed(2),
      );

      /* -------------------------------- */
      /* PAYMENT DISTRIBUTION */
      /* -------------------------------- */

      let paidRegFee = 0;
      let paidGST = 0;
      let paidStock = 0;

      let remainingStock = finalTotal - payingAmount;

      if (payingAmount > 0) {
        let remainingPay = payingAmount;

        paidStock = remainingPay * 0.7;
        // 30% platform fee INCLUDING GST
        let paidRegFeeWithGst = remainingPay * 0.3;

        paidRegFee = paidRegFeeWithGst / (1 + gstRate);
        paidGST = paidRegFeeWithGst - paidRegFee;
      }

      return {
        grossAmount, // original
        discountedTotal: finalTotal, // after discount
        baseAmount: (Number(regFee) + Number(stock)).toFixed(2),
        stock,
        regFee,
        gstAddedAmount,
        totalAmount,

        discountApplied: grossAmount - finalTotal,

        payingAmount,

        paidRegFee: paidRegFee.toFixed(2),
        paidGST: paidGST.toFixed(2),
        paidStock: paidStock.toFixed(2),

        remainingAmount: remainingStock,
      };
    };

    const getAmountDetails = () => {
      if (!paymentData) return null;

      let grossAmount = 0;

      if (partnerType === "channelPartnerPlus") {
        grossAmount = Number(selectedGrossAmount) || 0;
      } else {
        const level = getLevel();
        const currLevel = level.replace("-partner", "");

        if (currLevel === "district") {
          grossAmount = formData.isMetro
            ? paymentData.districtMetro
            : paymentData.districtNonMetro;
        } else if (currLevel === "pincode") {
          grossAmount = formData.isMetro
            ? paymentData.pincodeMetro
            : paymentData.pincodeNonMetro;
        } else if (currLevel === "retailer") {
          grossAmount = formData.isMetro
            ? paymentData.retailerMetro
            : paymentData.retailerNonMetro;
        } else {
          grossAmount = paymentData[currLevel] || 0;
        }
      }

      return calculateAmountDetails(grossAmount, payingAmount);
    };

    const getPartnerApplyAddress = () => {
      if (isPlusMode) return null; // or "" — as per your backend requirement
      const level = getLevel();
      switch (level) {
        case "country-partner":
          return formData.country || "";
        case "state-partner":
          return formData.state || "";
        case "district-partner":
          return formData.district?.replace(": Metro", "") || "";
        case "pincode-partner":
          return formData.pincode || "";
        case "retailer":
          return formData.pincode || "";
        default:
          return "";
      }
    };
    // NEW: Open form directly when in plus mode (on mount)
    useEffect(() => {
      if (isPlusMode && show) {
        setShowModal(false);
        setShowFormModal(true);
      }
    }, [isPlusMode, showFormModal, show]);
    const fetchPaymentData = async () => {
      try {
        const response = await axios.get(
          `${base_url}v1/user/partnerPaymentData`,
        );
        setPaymentData(
          window.location.pathname === "/signup/channelpartnerplus"
            ? response.data.data.partnerPlusPayment
            : response.data.data.partnerPayment,
        );
      } catch (error) {
        console.error("Error fetching payment data:", error);
      }
    };
    const verifyReferralCode = async (referralCode) => {
      try {
        const response = await axios.post(
          `${base_url}v1/user/verifyReferralCode`,
          {
            referralCode,
            partnerApplyAddress: getPartnerApplyAddress(),
          },
        );
        setReferralData(response.data.data);
      } catch (error) {
        console.error("Error verifying referral code:", error);
        setReferralData(null);
      }
    };
    useEffect(() => {
      const handler = setTimeout(() => {
        if (referralCodeInput && step === 2) {
          verifyReferralCode(referralCodeInput);
        }
      }, 500);
      return () => clearTimeout(handler);
    }, [referralCodeInput, step]);

    useEffect(() => {
      if (!setFieldValueRef.current) return;
      const details = getAmountDetails();
      console.log(details);
      if (!details) return;
      setFieldValueRef.current("amount", details.totalAmount);
      setFieldValueRef.current("grossAmount", details.grossAmount);
      setFieldValueRef.current("actualAmount", details.baseAmount);
      setFieldValueRef.current("gstAddedAmount", details.gstAddedAmount);
    }, [step, paymentData, couponDetails, selectedGrossAmount]);

    const handleNextStep = async (values, setSubmitting) => {
      if (step === 0) {
        setStep(1);
      } else if (step === 1) {
        setStep(2);
      } else if (step === 2) {
        await fetchPaymentData();
        setStep(3);
      } else if (step === 3) {
        setStep(4);
      }
      setSubmitting(false);
    };
    return (
      <Modal
        show={showFormModal}
        onHide={handleCloseFormModal}
        centered
        backdrop="static"
        dialogClassName="modal-90w md:modal-lg"
      >
        <Modal.Body className="p-4 md:p-6">
          <div className="relative bg-white rounded-lg">
            <button
              onClick={handleCloseFormModal}
              className="absolute top-2 right-2 bg-transparent border-0"
            >
              <FaTimes size={16} color="#666" />
            </button>
            {!showIntro && !isRegistered && (
              <div className="text-center mb-4">
                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                  Channel Partner {isPlusMode ? "Plus" : ""} Sign Up
                </h3>
                <p className="text-gray-500 text-xs md:text-sm">
                  {step === 0
                    ? "Fill in your details to sign up as a Partner"
                    : step === 1
                      ? "Enter bank details and KYC documents (Pan Card Only required)"
                      : step === 2
                        ? "Enter referral code (optional)"
                        : step === 3
                          ? "Scan the QR code and enter transaction details (All fields required)"
                          : "Review and agree to the terms and conditions Required"}
                </p>
                {!isPlusMode && isCurrentStepRestricted() && (
                  <p className="text-red-600 text-xs md:text-sm mt-2">
                    Warning: The selected{" "}
                    {steps[currentStep]
                      .toLowerCase()
                      .replace("(required)", "")
                      .replace("select ", "")}{" "}
                    is restricted and cannot be applied for directly. You can
                    apply as a retailer or proceed to the next step.
                  </p>
                )}
              </div>
            )}
            {isRegistered ? (
              <div className="text-center py-6">
                <h2 className="text-lg md:text-xl font-bold text-green-600">
                  Registration Successful
                </h2>
                <svg
                  className="w-10 h-10 mx-auto text-green-600 mt-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            ) : (
              <>
                {!isPlusMode && !formEnabled && step === 0 && (
                  <div className="mb-4 p-4 bg-red-100 text-red-600 rounded-lg font-['Poppins'] text-sm">
                    The selected{" "}
                    {steps[currentStep]
                      .toLowerCase()
                      .replace("(required)", "")
                      .replace("select ", "")}{" "}
                    is not available or restricted. Please choose an available,
                    non-restricted option to proceed with the application, or
                    continue to the next step.
                  </div>
                )}
                {showIntro && (
                  <div className="p-4 md:p-6 bg-white rounded-lg shadow-md">
                    <h2 className="text-xl md:text-2xl font-bold text-center mb-6 bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                      Channel Partner Plus Information
                    </h2>

                    <div className="text-center mb-8">
                      <p className="text-gray-700 text-sm md:text-sm mb-4">
                        Channel Partner Plus is a{" "}
                        <strong>virtual partner program</strong> designed to
                        provide attractive incentives and benefits for your
                        investment.
                      </p>
                      <ul className="list-disc list-inside text-left max-w-md mx-auto text-gray-700 text-sm md:text-sm space-y-2">
                        <li>
                          Receive{" "}
                          <strong>
                            3% to 4.5% Business Promotion Incentive (BPI)
                          </strong>{" "}
                          on the amount paid, disbursed over 36 months.
                        </li>
                        <li>
                          Earn <strong>10% referral wallet incentive</strong>{" "}
                          when you refer Channel Partners, Channel Partner Plus,
                          or others—the incentive is 10% of the amount they pay.
                        </li>
                        <li>
                          Get an <strong>instant 3.5% CashBack</strong> credited
                          to your wallet based on your paid amount.
                        </li>
                      </ul>
                    </div>

                    <div className="mt-8 px-2 sm:px-0">
                      <h3 className="text-xl md:text-2xl font-bold text-center mb-4 text-gray-800">
                        Choose Your Investment & Unlock Your Partner Level
                      </h3>

                      <p className="text-center text-gray-600 text-sm md:text-sm mb-8 max-w-3xl mx-auto">
                        Your investment amount determines your partner level.
                        Higher investments unlock greater earning potential,
                        exclusive rights, and stronger incentives.
                        Metro/non-metro classification is finalized by the
                        company.
                      </p>

                      {/* Row 1 - 3 cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-6 mb-6">
                        {/* Retailer */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                          <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 text-white text-center font-bold text-lg">
                            Retailer
                          </div>
                          <div className="p-2.5 flex flex-col flex-grow text-center">
                            <div className="text-xl font-bold mb-3 text-green-700">
                              ₹5,000 – ₹15,000
                            </div>
                            <p className="text-sm text-gray-700">
                              Starting from ₹5,000 (non-metro)
                              <br />
                              Up to ₹15,000 → metro retailer eligible
                            </p>
                            <div className="mt-auto pt-4 text-xs text-gray-500">
                              Entry Level
                            </div>
                          </div>
                        </div>

                        {/* Pincode Partner */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-white text-center font-bold text-lg">
                            Pincode Partner
                          </div>
                          <div className="p-2.5 flex flex-col flex-grow text-center">
                            <div className="text-xl font-bold mb-3 text-blue-700">
                              ₹50,000 – ₹5 Lakh
                            </div>
                            <p className="text-sm text-gray-700">
                              ₹50,000 → non-metro pincode
                              <br />
                              Up to ₹5 Lakh → metro pincode eligible
                            </p>
                            <div className="mt-auto pt-4 text-xs text-gray-500">
                              Local Level
                            </div>
                          </div>
                        </div>

                        {/* District Partner */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                          <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white text-center font-bold text-lg">
                            District Partner
                          </div>
                          <div className="p-2 flex flex-col flex-grow text-center">
                            <div className="text-xl font-bold mb-3 text-purple-700">
                              ₹5 Lakh – ₹40 Lakh
                            </div>
                            <p className="text-sm text-gray-700">
                              ₹5 Lakh → non-metro district
                              <br />
                              Up to ₹40 Lakh → metro district eligible
                            </p>
                            <div className="mt-auto pt-4 text-xs text-gray-500">
                              District Level
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Row 2 - 2 cards (State + Country) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                        {/* State Partner */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                          <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 text-white text-center font-bold text-lg">
                            State Partner
                          </div>
                          <div className="p-2.5 flex flex-col flex-grow text-center">
                            <div className="text-xl font-bold mb-3 text-amber-700">
                              ₹60 Lakh – ₹1.2 Crore
                            </div>
                            <p className="text-sm text-gray-700">
                              ₹60 Lakh → small states
                              <br />
                              ₹1.2 Crore → large states
                            </p>
                            <div className="mt-auto pt-4 text-xs text-gray-500">
                              State Level
                            </div>
                          </div>
                        </div>

                        {/* Country Partner */}
                        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                          <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 text-white text-center font-bold text-lg">
                            Country Partner
                          </div>
                          <div className="p-2.5 flex flex-col flex-grow text-center">
                            <div className="text-xl font-bold mb-3 text-red-700">
                              Up to ₹18 Crore
                            </div>
                            <p className="text-sm text-gray-700">
                              Highest investment tier
                              <br />
                              Pan-India business rights
                            </p>
                            <div className="mt-auto pt-4 text-xs text-gray-500">
                              Top Level
                            </div>
                          </div>
                        </div>
                      </div>

                      <p className="text-center text-xs text-gray-500 mt-8">
                        * Final metro/non-metro eligibility and partner level
                        confirmation is subject to company verification and
                        current policy.
                      </p>
                    </div>
                    <div className="text-center mt-8">
                      <Button
                        onClick={() => setShowIntro(false)}
                        className="px-6 py-2 bg-[#00C70D] text-white font-['Poppins'] rounded-lg hover:bg-green-700 transition-colors duration-200"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
                {!showIntro && (
                  <Formik
                    initialValues={{
                      name: "",
                      email: "",
                      phone: "",
                      partnerApplyAddress: getPartnerApplyAddress(),
                      resAddress_line: "",
                      resCountry: "",
                      resState: "",
                      resCity: "",
                      resDistrict: "",
                      resPincode: "",
                      correAddress_line: "",
                      correCountry: "",
                      correState: "",
                      correCity: "",
                      correDistrict: "",
                      correPincode: "",
                      dob: "",
                      gender: "",
                      profilePhoto: null,
                      businessName: "",
                      gstNo: "",
                      bankDetails: {
                        holderName: "",
                        bankName: "",
                        accountNo: "",
                        ifscCode: "",
                      },
                      paidAmount: "",
                      aadharCard: null,
                      aadharCardNo: "", // New field for Aadhar Card Number
                      panCard: null,
                      panCardNo: "", // New field for PAN Card Number
                      referralCode: referralCodeInput,
                      // transactionId: "",
                      // transferType: "",
                      amount: "",
                      grossAmount: "",
                      actualAmount: "",
                      gstAddedAmount: "",
                      screenshot: null,
                      agreement: false,
                    }}
                    validationSchema={validationSchema[step]}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                      if (step < formSteps.length - 1) {
                        handleNextStep(values, setSubmitting);
                        return;
                      }
                      try {
                        console.log(values);
                        const formDataToSend = new FormData();
                        if (isPlusMode) {
                          formDataToSend.append("mainProductId", ""); // null / empty
                          formDataToSend.append("partnerApplyAddress", ""); // null / empty
                          formDataToSend.append("level", "partnerPlus"); // or your backend value
                        } else {
                          formDataToSend.append(
                            "mainProductId",
                            formData.category,
                          );
                          formDataToSend.append(
                            "partnerApplyAddress",
                            values.partnerApplyAddress,
                          );
                          formDataToSend.append("level", getLevel());
                          formDataToSend.append(
                            "paidAmount",
                            values.paidAmount,
                          );
                        }
                        formDataToSend.append("name", values.name);
                        formDataToSend.append("email", values.email);
                        formDataToSend.append("phone", values.phone);
                        formDataToSend.append(
                          "address_line",
                          values.resAddress_line,
                        );
                        formDataToSend.append("country", values.resCountry);
                        formDataToSend.append("state", values.resState);
                        formDataToSend.append("city", values.resCity);
                        formDataToSend.append("district", values.resDistrict);
                        formDataToSend.append("pincode", values.resPincode);
                        formDataToSend.append(
                          "correAddress_line",
                          values.correAddress_line,
                        );
                        formDataToSend.append(
                          "correCountry",
                          values.correCountry,
                        );
                        formDataToSend.append("correState", values.correState);
                        formDataToSend.append("correCity", values.correCity);
                        formDataToSend.append(
                          "correDistrict",
                          values.correDistrict,
                        );
                        formDataToSend.append("partnerType", partnerType);
                        formDataToSend.append(
                          "correPincode",
                          values.correPincode,
                        );
                        formDataToSend.append("dob", values.dob);
                        formDataToSend.append("gender", values.gender);
                        formDataToSend.append(
                          "profilePhoto",
                          values.profilePhoto,
                        );
                        if (values.businessName) {
                          formDataToSend.append(
                            "businessName",
                            values.businessName,
                          );
                        }
                        if (values.gstNo) {
                          formDataToSend.append("gstNo", values.gstNo);
                        }
                        formDataToSend.append(
                          "holderName",
                          values.bankDetails.holderName || "",
                        );
                        formDataToSend.append(
                          "accountNo",
                          values.bankDetails.accountNo || "",
                        );
                        formDataToSend.append(
                          "ifscCode",
                          values.bankDetails.ifscCode || "",
                        );
                        formDataToSend.append(
                          "bankName",
                          values.bankDetails.bankName || "",
                        );
                        if (values.aadharCard && values.aadharCardNo) {
                          formDataToSend.append(
                            "aadharCard",
                            values.aadharCard,
                          );
                          formDataToSend.append(
                            "aadharCardNo",
                            values.aadharCardNo,
                          ); // Append new field
                        }
                        formDataToSend.append("panCard", values.panCard);
                        formDataToSend.append("panCardNo", values.panCardNo); // Append new field
                        if (values.referralCode) {
                          formDataToSend.append("referredBy", referralData._id);
                        }
                        formDataToSend.append("amount", values.amount);
                        formDataToSend.append(
                          "grossAmount",
                          values.actualAmount,
                        );
                        formDataToSend.append(
                          "gstAddedAmount",
                          values.gstAddedAmount,
                        );
                        if (values.screenshot) {
                          formDataToSend.append(
                            "screenshot",
                            values.screenshot,
                          );
                        }
                        if (discount.id) {
                          formDataToSend.append("couponId", discount.id);
                        }
                        formDataToSend.append("agreement", values.agreement);

                        const url = `${base_url}v1/user/userPartner/${partnerType === "channelPartner" ? "signUp" : "userPartnerPartnerSignUp"}`;
                        const response = await axios.post(url, formDataToSend, {
                          headers: {
                            "Content-Type": "multipart/form-data",
                          },
                        });

                        if (!response.data.paymentSessionId) {
                          resetForm();
                          setIsRegistered(true);
                        } else {
                          // 4️⃣ Redirect to Cashfree
                          const cashfree = new window.Cashfree();
                          cashfree
                            .checkout({
                              paymentSessionId: response.data.paymentSessionId,
                              redirectTarget: "_self",
                              mode: "production", // ✅ REQUIRED
                            })
                            .then((result) => {
                              console.log("Checkout result:", result);
                            })
                            .catch((err) => {
                              console.error("Checkout error:", err);
                            });
                        }

                        resetForm();
                      } catch (error) {
                        console.error("Registration error:", error);
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ isSubmitting, setFieldValue, values, errors }) => {
                      console.log(values);
                      setFieldValueRef.current = setFieldValue;
                      const amountBreakdown = getAmountDetails();
                      console.log(amountBreakdown);
                      const minimumPayable =
                        (amountBreakdown?.regFee || 0) +
                          (amountBreakdown?.gstAddedAmount || 0) || "";
                      if (!amountBreakdown) values.amount = "";
                      console.log(errors);
                      return (
                        <Form className="flex flex-col gap-3 font-['Poppins']">
                          {step === 0 && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Name <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    name="name"
                                    placeholder="Enter Name"
                                    disabled={!formEnabled}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name="name"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Email{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    type="email"
                                    name="email"
                                    placeholder="Enter Email"
                                    disabled={!formEnabled}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Phone{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    name="phone"
                                    placeholder="Enter Phone Number"
                                    disabled={!formEnabled}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                  />
                                  <ErrorMessage
                                    name="phone"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Date of Birth{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    type="date"
                                    name="dob"
                                    disabled={!formEnabled}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                    max={new Date().toISOString().split("T")[0]}
                                  />
                                  <ErrorMessage
                                    name="dob"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Gender{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    as="select"
                                    name="gender"
                                    disabled={!formEnabled}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                  >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                  </Field>
                                  <ErrorMessage
                                    name="gender"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Profile Photo{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <button
                                    type="button"
                                    className={`w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-200 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                    disabled={!formEnabled}
                                    onClick={() =>
                                      document
                                        .getElementById("profilePhoto")
                                        .click()
                                    }
                                  >
                                    Upload
                                  </button>
                                  <input
                                    type="file"
                                    id="profilePhoto"
                                    name="profilePhoto"
                                    accept="image/*"
                                    disabled={!formEnabled}
                                    className="hidden"
                                    onChange={(event) => {
                                      if (formEnabled) {
                                        setFieldValue(
                                          "profilePhoto",
                                          event.currentTarget.files[0],
                                        );
                                      }
                                    }}
                                  />
                                  {values.profilePhoto && (
                                    <div className="mt-1 text-xs text-green-600">
                                      {values.profilePhoto.name ||
                                        "File uploaded"}
                                    </div>
                                  )}
                                  <ErrorMessage
                                    name="profilePhoto"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Level{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    as="select"
                                    name="level"
                                    disabled={true}
                                    className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                      !formEnabled
                                        ? "opacity-75 cursor-not-allowed"
                                        : ""
                                    }`}
                                    value={getLevel()}
                                  >
                                    <option value="">Select Level</option>
                                    <option value="country-partner">
                                      Country Partner
                                    </option>
                                    <option value="state-partner">
                                      State Partner
                                    </option>
                                    <option value="district-partner">
                                      District Partner
                                    </option>
                                    <option value="pincode-partner">
                                      Pincode Partner
                                    </option>
                                    <option value="retailer">Retailer</option>
                                    <option value="partnerPlus">
                                      Channel Partner Plus
                                    </option>
                                  </Field>
                                  <ErrorMessage
                                    name="level"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                {!isPlusMode && (
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Selected{" "}
                                      {getLevel()
                                        .replace("-partner", " ")
                                        .replace(/^\w/, (c) =>
                                          c.toUpperCase(),
                                        )}{" "}
                                      For Partner Registration{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="partnerApplyAddress"
                                      placeholder="Enter Partner Address"
                                      disabled={true}
                                      className={`modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500 ${
                                        !formEnabled
                                          ? "opacity-75 cursor-not-allowed"
                                          : ""
                                      }`}
                                    />
                                    <ErrorMessage
                                      name="partnerApplyAddress"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="mt-6">
                                <h3 className="text-sm md:text-base font-semibold mb-2">
                                  Residential Address{" "}
                                  <span className="text-red-500">*</span>
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Street Address{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resAddress_line"
                                      placeholder="Enter Street Address"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resAddress_line"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      City{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resCity"
                                      placeholder="Enter City"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resCity"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      District{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resDistrict"
                                      placeholder="Enter District"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resDistrict"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Pincode{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resPincode"
                                      placeholder="Enter Pincode"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resPincode"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Country{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resCountry"
                                      placeholder="Enter Country"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resCountry"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      State{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="resState"
                                      placeholder="Enter State"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="resState"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                </div>
                              </div>
                              {/* Checkbox */}
                              <div className="mt-4">
                                <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={useSameAddress}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setUseSameAddress(checked);
                                      if (checked) {
                                        setFieldValue(
                                          "correAddress_line",
                                          values.resAddress_line || "",
                                        );
                                        setFieldValue(
                                          "correCountry",
                                          values.resCountry || "",
                                        );
                                        setFieldValue(
                                          "correState",
                                          values.resState || "",
                                        );
                                        setFieldValue(
                                          "correCity",
                                          values.resCity || "",
                                        );
                                        setFieldValue(
                                          "correDistrict",
                                          values.resDistrict || "",
                                        );
                                        setFieldValue(
                                          "correPincode",
                                          values.resPincode || "",
                                        );
                                      } else {
                                        // Optional: clear correspondence fields when unchecked
                                        setFieldValue("correAddress_line", "");
                                        setFieldValue("correCountry", "");
                                        setFieldValue("correState", "");
                                        setFieldValue("correCity", "");
                                        setFieldValue("correDistrict", "");
                                        setFieldValue("correPincode", "");
                                      }
                                    }}
                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                                  />
                                  <span>
                                    Same as Residential Address (for Business /
                                    Office Address)
                                  </span>
                                </label>
                              </div>
                              <div className="mt-3">
                                <h3 className="text-sm md:text-base font-semibold mb-2">
                                  Business Name / Office Address{" "}
                                  <span className="text-red-500">*</span>
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Street Address{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correAddress_line"
                                      placeholder="Enter Street Address"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correAddress_line"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      City{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correCity"
                                      placeholder="Enter City"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correCity"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      District{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correDistrict"
                                      placeholder="Enter District"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correDistrict"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Pincode{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correPincode"
                                      placeholder="Enter Pincode"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correPincode"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Country{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correCountry"
                                      placeholder="Enter Country"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correCountry"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      State{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="correState"
                                      placeholder="Enter State"
                                      className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    />
                                    <ErrorMessage
                                      name="correState"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          {step === 1 && (
                            <>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    PAN Card Number{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <Field
                                    type="text"
                                    name="panCardNo"
                                    placeholder="Enter PAN Card Number"
                                    className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                  />
                                  <ErrorMessage
                                    name="panCardNo"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    PAN Card{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <button
                                    type="button"
                                    className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-200"
                                    onClick={() =>
                                      document.getElementById("panCard").click()
                                    }
                                  >
                                    Upload
                                  </button>
                                  <input
                                    type="file"
                                    id="panCard"
                                    name="panCard"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(event) =>
                                      setFieldValue(
                                        "panCard",
                                        event.currentTarget.files[0],
                                      )
                                    }
                                  />
                                  {values.panCard && (
                                    <div className="mt-1 text-xs text-green-600">
                                      {values.panCard.name || "File uploaded"}
                                    </div>
                                  )}
                                  <ErrorMessage
                                    name="panCard"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          {step === 2 && (
                            <>
                              <div className="grid grid-cols-1 gap-4">
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Referral Code (Optional)
                                  </label>
                                  <Field
                                    type="text"
                                    name="referralCode"
                                    placeholder="Enter Referral Code"
                                    className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "referralCode",
                                        e.target.value,
                                      );
                                      setReferralCodeInput(e.target.value);
                                    }}
                                  />
                                  <ErrorMessage
                                    name="referralCode"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                                {referralData ? (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs md:text-xs font-semibold">
                                        Name
                                      </label>
                                      <input
                                        type="text"
                                        value={referralData.name}
                                        disabled
                                        className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg opacity-75 cursor-not-allowed"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs md:text-xs font-semibold">
                                        Mobile
                                      </label>
                                      <input
                                        type="text"
                                        value={
                                          referralData.phone ||
                                          referralData.mobile
                                        }
                                        disabled
                                        className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg opacity-75 cursor-not-allowed"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {values.referralCode && (
                                      <div className="text-red-500 text-xs mt-1">
                                        Invalid Referral Code
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </>
                          )}
                          {step === 3 && (
                            <>
                              {isPlusMode && (
                                <div className="mt-8 px-2 sm:px-0">
                                  <h3 className="text-xl md:text-2xl font-bold text-center mb-4 text-gray-800">
                                    Choose Your Investment & Unlock Your Partner
                                    Level
                                  </h3>

                                  <p className="text-center text-gray-600 text-sm md:text-sm mb-8 max-w-3xl mx-auto">
                                    Your investment amount determines your
                                    partner level. Higher investments unlock
                                    greater earning potential, exclusive rights,
                                    and stronger incentives. Metro/non-metro
                                    classification is finalized by the company.
                                  </p>

                                  {/* Row 1 - 3 cards */}
                                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-6 mb-6">
                                    {/* Retailer */}
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                                      <div className="bg-gradient-to-r from-green-500 to-green-600 px-4 py-3 text-white text-center font-bold text-lg">
                                        Retailer
                                      </div>
                                      <div className="p-2.5 flex flex-col flex-grow text-center">
                                        <div className="text-xl font-bold mb-3 text-green-700">
                                          ₹5,000 – ₹15,000
                                        </div>
                                        <p className="text-sm text-gray-700">
                                          Starting from ₹5,000 (non-metro)
                                          <br />
                                          Up to ₹15,000 → metro retailer
                                          eligible
                                        </p>
                                        <div className="mt-auto pt-4 text-xs text-gray-500">
                                          Entry Level
                                        </div>
                                      </div>
                                    </div>

                                    {/* Pincode Partner */}
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-white text-center font-bold text-lg">
                                        Pincode Partner
                                      </div>
                                      <div className="p-2.5 flex flex-col flex-grow text-center">
                                        <div className="text-xl font-bold mb-3 text-blue-700">
                                          ₹50,000 – ₹5 Lakh
                                        </div>
                                        <p className="text-sm text-gray-700">
                                          ₹50,000 → non-metro pincode
                                          <br />
                                          Up to ₹5 Lakh → metro pincode eligible
                                        </p>
                                        <div className="mt-auto pt-4 text-xs text-gray-500">
                                          Local Level
                                        </div>
                                      </div>
                                    </div>

                                    {/* District Partner */}
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                                      <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white text-center font-bold text-lg">
                                        District Partner
                                      </div>
                                      <div className="p-2 flex flex-col flex-grow text-center">
                                        <div className="text-xl font-bold mb-3 text-purple-700">
                                          ₹5 Lakh – ₹40 Lakh
                                        </div>
                                        <p className="text-sm text-gray-700">
                                          ₹5 Lakh → non-metro district
                                          <br />
                                          Up to ₹40 Lakh → metro district
                                          eligible
                                        </p>
                                        <div className="mt-auto pt-4 text-xs text-gray-500">
                                          District Level
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Row 2 - 2 cards (State + Country) */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                                    {/* State Partner */}
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                                      <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 text-white text-center font-bold text-lg">
                                        State Partner
                                      </div>
                                      <div className="p-2.5 flex flex-col flex-grow text-center">
                                        <div className="text-xl font-bold mb-3 text-amber-700">
                                          ₹60 Lakh – ₹1.2 Crore
                                        </div>
                                        <p className="text-sm text-gray-700">
                                          ₹60 Lakh → small states
                                          <br />
                                          ₹1.2 Crore → large states
                                        </p>
                                        <div className="mt-auto pt-4 text-xs text-gray-500">
                                          State Level
                                        </div>
                                      </div>
                                    </div>

                                    {/* Country Partner */}
                                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
                                      <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 text-white text-center font-bold text-lg">
                                        Country Partner
                                      </div>
                                      <div className="p-2.5 flex flex-col flex-grow text-center">
                                        <div className="text-xl font-bold mb-3 text-red-700">
                                          Up to ₹18 Crore
                                        </div>
                                        <p className="text-sm text-gray-700">
                                          Highest investment tier
                                          <br />
                                          Pan-India business rights
                                        </p>
                                        <div className="mt-auto pt-4 text-xs text-gray-500">
                                          Top Level
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <p className="text-center text-xs text-gray-500 mt-8">
                                    * Final metro/non-metro eligibility and
                                    partner level confirmation is subject to
                                    company verification and current policy.
                                  </p>
                                </div>
                              )}
                              <div className="mt-4">
                                <h3 className="text-sm md:text-lg font-semibold mb-2">
                                  Amount Breakdown
                                </h3>

                                {amountBreakdown && (
                                  <div className="bg-gray-50 p-4 rounded-lg border">
                                    {/* MAIN AMOUNT */}

                                    <h4 className="font-semibold text-gray-800 mb-2">
                                      Main Amount (Total Payable)
                                    </h4>

                                    <div className="flex justify-between mb-1">
                                      <span>Base Amount</span>
                                      <span>
                                        ₹
                                        {formatIndianNumber(
                                          amountBreakdown.grossAmount,
                                        )}
                                      </span>
                                    </div>

                                    {/* DISCOUNT */}
                                    {amountBreakdown.discountApplied > 0 && (
                                      <>
                                        <div className="flex justify-between mb-1">
                                          <span>Coupon Discount</span>
                                          <span className="text-red-600">
                                            - ₹
                                            {formatIndianNumber(
                                              amountBreakdown.discountApplied,
                                            )}
                                          </span>
                                        </div>
                                        {/* FINAL PAYABLE */}
                                        <div className="flex justify-between mb-2 font-semibold">
                                          <span>Amount After Discount</span>
                                          <span className="text-green-700">
                                            ₹
                                            {formatIndianNumber(
                                              amountBreakdown.discountedTotal,
                                            )}
                                          </span>
                                        </div>
                                      </>
                                    )}
                                    {}

                                    {partnerType !== "channelPartnerPlus" && (
                                      <>
                                        <div className="flex justify-between mb-1">
                                          <span>
                                            Actual Amount (Registration Fee +
                                            Stock Issued)
                                          </span>
                                          <span>
                                            ₹
                                            {formatIndianNumber(
                                              amountBreakdown.baseAmount || 0,
                                            )}
                                          </span>
                                        </div>
                                        <div className="flex justify-between mb-1">
                                          <span>Stock Issued</span>
                                          <span className="text-green-600">
                                            ₹
                                            {formatIndianNumber(
                                              amountBreakdown.stock,
                                            )}
                                          </span>
                                        </div>
                                      </>
                                    )}

                                    <div className="flex justify-between mb-1">
                                      <span>Registration Fee</span>
                                      <span className="text-blue-600">
                                        ₹
                                        {formatIndianNumber(
                                          amountBreakdown.regFee,
                                        )}
                                      </span>
                                    </div>

                                    <div className="flex justify-between mb-1">
                                      <span>GST (18%)</span>
                                      <span className="text-orange-600">
                                        ₹
                                        {formatIndianNumber(
                                          amountBreakdown.gstAddedAmount,
                                        )}
                                      </span>
                                    </div>

                                    <div className="flex justify-between border-t pt-2 mt-2 font-bold">
                                      <span>Total Amount</span>
                                      <span>
                                        ₹
                                        {formatIndianNumber(
                                          amountBreakdown.totalAmount,
                                        )}
                                      </span>
                                    </div>

                                    {/* PAYMENT SECTION */}

                                    {amountBreakdown.payingAmount > 0 &&
                                      partnerType !== "channelPartnerPlus" && (
                                        <>
                                          <div className="border-t my-3"></div>

                                          <h4 className="font-semibold text-gray-800 mb-2">
                                            Paying Now
                                          </h4>

                                          <div className="flex justify-between mb-1">
                                            <span>Paying Amount</span>
                                            <span>
                                              ₹
                                              {formatIndianNumber(
                                                amountBreakdown.payingAmount,
                                              )}
                                            </span>
                                          </div>

                                          <div className="flex justify-between mb-1">
                                            <span>Registration Fee Paid</span>
                                            <span className="text-blue-600">
                                              ₹
                                              {formatIndianNumber(
                                                amountBreakdown.paidRegFee,
                                              )}
                                            </span>
                                          </div>

                                          <div className="flex justify-between mb-1">
                                            <span>GST Paid</span>
                                            <span className="text-orange-600">
                                              ₹
                                              {formatIndianNumber(
                                                amountBreakdown.paidGST,
                                              )}
                                            </span>
                                          </div>

                                          <div className="flex justify-between mb-1">
                                            <span>Stock Paid</span>
                                            <span className="text-green-600">
                                              ₹
                                              {formatIndianNumber(
                                                amountBreakdown.paidStock,
                                              )}
                                            </span>
                                          </div>

                                          <div className="flex justify-between border-t pt-2 mt-2 font-bold text-red-600">
                                            <span>Remaining Amount</span>
                                            <span>
                                              ₹
                                              {formatIndianNumber(
                                                amountBreakdown.remainingAmount,
                                              )}
                                            </span>
                                          </div>
                                        </>
                                      )}
                                  </div>
                                )}
                              </div>
                              {partnerType === "channelPartner" && (
                                <div className="mb-3">
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Coupon Code
                                  </label>

                                  <div className="d-flex gap-2">
                                    <input
                                      className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                      value={couponCode}
                                      onChange={(e) =>
                                        setCouponCode(e.target.value)
                                      }
                                      placeholder="Enter coupon code"
                                      disabled={couponDetails}
                                    />

                                    <Button
                                      variant="success"
                                      onClick={handleApplyCoupon}
                                      disabled={!couponCode || couponDetails}
                                    >
                                      Apply
                                    </Button>
                                  </div>

                                  {couponError && (
                                    <div className="text-danger small mt-1">
                                      {couponError}
                                    </div>
                                  )}

                                  {couponDetails && (
                                    <div className="mt-3 p-2.5 border border-success border-opacity-50 rounded bg-success-subtle d-flex flex-wrap justify-content-between align-items-center gap-2">
                                      <div>
                                        <h6 className="mb-1 text-success fw-bold">
                                          {couponDetails.title}
                                        </h6>

                                        <p className="mb-0 small text-success">
                                          <strong>Discount:</strong>{" "}
                                          {couponDetails.discountType ===
                                          "percentage"
                                            ? `${couponDetails.discountValue}% off`
                                            : `₹${couponDetails.discountValue} off`}
                                        </p>
                                      </div>

                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleRemoveCoupon}
                                        disabled={isApplyingCoupon}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}

                              {partnerType === "channelPartner" && (
                                <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
                                  <h3 className="text-sm md:text-lg font-semibold mb-3">
                                    Payment Option
                                  </h3>

                                  {/* Installment Checkbox */}
                                  {partnerType !== "channelPartnerPlus" && (
                                    <div className="flex items-center mb-3">
                                      <input
                                        type="checkbox"
                                        id="installment"
                                        checked={isInstallment}
                                        onChange={(e) => {
                                          setIsInstallment(e.target.checked);
                                          if (!e.target.checked)
                                            setPayingAmount("");
                                        }}
                                        className="mr-2"
                                      />
                                      <label
                                        htmlFor="installment"
                                        className="text-sm text-gray-700"
                                      >
                                        Pay in Installment
                                      </label>
                                    </div>
                                  )}

                                  {/* Paying Amount Input */}
                                  {isInstallment && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Minimum payable amount is
                                      </label>

                                      <Field
                                        type="number"
                                        placeholder="Enter amount you want to pay"
                                        value={payingAmount}
                                        min={1}
                                        max={amountBreakdown?.totalAmount}
                                        onChange={(e) => {
                                          let value = Number(e.target.value);
                                          if (value > 0) {
                                            setPayingAmount(value);
                                          } else if (value < 1) {
                                            setPayingAmount("");
                                          }
                                          setFieldValue("paidAmount", value);
                                        }}
                                        className="modern-input w-full pl-10 p-2.5 text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                      />
                                      {payingAmount >
                                        amountBreakdown?.totalAmount && (
                                        <p className="text-red-500 text-xs mt-1">
                                          Maximum payment required is ₹
                                          {formatIndianNumber(
                                            amountBreakdown?.totalAmount,
                                          )}
                                        </p>
                                      )}
                                      {amountBreakdown && (
                                        <p className="text-xs text-gray-500 mt-1">
                                          Total Payable: ₹
                                          {formatIndianNumber(
                                            amountBreakdown.totalAmount,
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {partnerType === "channelPartnerPlus" && (
                                <div className="mt-6">
                                  <label className="block text-sm md:text-sm font-semibold mb-2">
                                    Enter Base Amount (Registration Amount)
                                  </label>
                                  <div className="relative">
                                    <Field
                                      type="text"
                                      name="amount" // ← we will use this field directly
                                      placeholder="Enter amount (min 5000, max 180000000)"
                                      min="5000"
                                      max="180000000"
                                      step="1000" // optional: allow increments of 1000
                                      className="modern-input w-full pl-10 p-2.5 text-sm border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedGrossAmount(val); // keep sync if needed elsewhere
                                        setFieldValue("amount", val); // update formik value
                                      }}
                                    />
                                  </div>
                                  {/* Helpful text + validation feedback */}
                                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                                    <p>• Minimum amount: ₹5,000</p>
                                    <p>
                                      • Maximum amount: ₹18,00,00,000 (18 Crore)
                                    </p>
                                    <p>
                                      • Amount should be in whole numbers (no
                                      decimals)
                                    </p>
                                  </div>
                                  {/* Show live error if out of range (optional but recommended) */}
                                  {values.grossAmount && (
                                    <>
                                      {Number(values.grossAmount) < 5000 && (
                                        <p className="mt-1 text-xs text-red-600">
                                          Amount must be at least ₹5,000
                                        </p>
                                      )}
                                      {Number(values.grossAmount) >
                                        180000000 && (
                                        <p className="mt-1 text-xs text-red-600">
                                          Amount cannot exceed ₹18 Crore
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Total Amount{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="number"
                                  name="amount"
                                  disabled={true}
                                  placeholder="Enter Amount"
                                  className="modern-input w-full mt-1 p-2.5 text-sm border-gray-300 bg-gray-100 rounded-lg"
                                />
                                <ErrorMessage
                                  name="amount"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              {((partnerType === "channelPartnerPlus" &&
                                values.amount > 1000000) ||
                                (partnerType === "channelPartner" &&
                                  (payingAmount === "" || payingAmount === 0
                                    ? values.amount > 1000000
                                    : payingAmount > 1000000))) && (
                                <div>
                                  {/* Instruction Message */}
                                  <div className="mt-4 p-2.5 bg-yellow-50 border border-yellow-300 rounded-lg text-sm text-gray-700">
                                    <p className="font-semibold text-yellow-700 mb-1">
                                      Important Payment Instruction
                                    </p>
                                    <p>
                                      If your payment amount is greater than{" "}
                                      <strong>10,00,000</strong>, please scan
                                      the QR code below and complete the payment
                                      directly. After making the payment, upload
                                      the transaction screenshot. Our company
                                      will review the payment and confirm your
                                      registration once the transaction is
                                      verified.
                                    </p>
                                  </div>

                                  <div className="mt-4 text-center">
                                    <h3 className="text-sm md:text-lg font-semibold mb-2">
                                      Scan to Pay
                                    </h3>

                                    <div className="inline-block p-4 bg-white border border-gray-200 rounded-lg shadow-md">
                                      <img
                                        src="/qr.JPG"
                                        alt="Payment QR Code"
                                        className="w-72 h-72 md:w-72 md:h-72 object-contain"
                                      />
                                      <p className="text-sm text-gray-600 mt-2">
                                        Scan this QR code to make the payment
                                      </p>
                                    </div>
                                  </div>

                                  <div>
                                    <label className="block text-xs md:text-xs font-semibold">
                                      Payment Screenshot{" "}
                                      <span className="text-red-500">*</span>
                                    </label>

                                    <button
                                      type="button"
                                      className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-200"
                                      onClick={() =>
                                        document
                                          .getElementById("screenshot")
                                          .click()
                                      }
                                    >
                                      Upload
                                    </button>

                                    <input
                                      type="file"
                                      id="screenshot"
                                      name="screenshot"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(event) =>
                                        setFieldValue(
                                          "screenshot",
                                          event.currentTarget.files[0],
                                        )
                                      }
                                    />

                                    {values.screenshot && (
                                      <div className="mt-1 text-xs text-green-600">
                                        {values.screenshot.name ||
                                          "File uploaded"}
                                      </div>
                                    )}

                                    <ErrorMessage
                                      name="screenshot"
                                      component="div"
                                      className="text-red-500 text-xs mt-1"
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          {step === 4 && (
                            <>
                              <div className="mt-4">
                                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                                  Terms and Conditions
                                </h3>
                                <p className="text-gray-500 text-xs md:text-sm mb-4">
                                  Terms and Conditions for Channel Partner
                                  Enrollment
                                </p>
                                <div className="max-w-3xl mx-auto p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
                                  <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                    Channel Partner Agreement Terms & Conditions
                                  </h2>
                                  <div className="max-h-[400px] overflow-y-auto p-4 bg-gray-50 rounded-lg scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-100 scrollbar-thumb-rounded-full">
                                    <div className="text-gray-700 font-['Poppins'] text-sm leading-relaxed">
                                      <p className="mb-4 text-gray-600">
                                        This{" "}
                                        <span className="font-semibold">
                                          Agreement
                                        </span>{" "}
                                        governs the enrollment and participation
                                        of individuals or entities (“Channel
                                        Partner”) in the distribution of the
                                        Global Digital Health Smart Card,
                                        offered by LifeOnPlus (“Company”). By
                                        enrolling as a Channel Partner, you
                                        agree to comply with and be bound by the
                                        following terms:
                                      </p>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          1
                                        </span>
                                        Eligibility & Enrollment
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          Channel Partner must be 18 years of
                                          age or above and legally competent to
                                          enter into this Agreement.
                                        </li>
                                        <li>
                                          Enrollment is subject to approval by
                                          the Company, and the Company reserves
                                          the right to reject or terminate any
                                          enrollment without assigning reasons.
                                        </li>
                                        <li>
                                          The Channel Partner must provide
                                          accurate details including PAN, GST
                                          (if applicable), and banking details
                                          for settlements.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          2
                                        </span>
                                        Roles & Responsibilities
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          The Channel Partner is authorized to
                                          promote, market, and distribute Global
                                          Digital Health Smart Cards strictly as
                                          per Company guidelines.
                                        </li>
                                        <li>
                                          Misrepresentation of product benefits,
                                          features, or unauthorized claims is
                                          strictly prohibited.
                                        </li>
                                        <li>
                                          The Channel Partner shall not engage
                                          in activities that may harm the
                                          reputation or goodwill of the Company.
                                        </li>
                                        <li>
                                          All marketing, training, and branding
                                          materials shall be provided/approved
                                          by the Company.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          3
                                        </span>
                                        Financial Terms
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          Channel Partner earnings shall be
                                          based on the Company’s distribution
                                          margin, referral structure, and wallet
                                          system as communicated from time to
                                          time.
                                        </li>
                                        <li>
                                          All settlements will be made net of
                                          applicable TDS, service fees, GST, and
                                          other statutory deductions.
                                        </li>
                                        <li>
                                          No fixed returns, interest, or
                                          guaranteed income is offered. Earnings
                                          depend solely on sales and business
                                          performance.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          4
                                        </span>
                                        Intellectual Property
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          The Channel Partner is granted a
                                          non-exclusive, non-transferable right
                                          to use the Company’s name, logo, and
                                          promotional materials solely for
                                          business purposes.
                                        </li>
                                        <li>
                                          Any misuse of Company’s intellectual
                                          property shall result in termination
                                          and legal action.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          5
                                        </span>
                                        Confidentiality
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          The Channel Partner shall maintain
                                          strict confidentiality of all Company
                                          data, agreements, pricing structures,
                                          and business models.
                                        </li>
                                        <li>
                                          Sharing or misusing Company
                                          information with competitors or third
                                          parties is prohibited.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          6
                                        </span>
                                        Termination
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          The Company reserves the right to
                                          suspend or terminate the partnership
                                          at any time in case of non-compliance
                                          with this Agreement.
                                        </li>
                                        <li>
                                          Upon termination, all rights to
                                          represent the Company or distribute
                                          products shall cease immediately.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          7
                                        </span>
                                        Dispute Resolution & Jurisdiction
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          Any disputes arising out of or in
                                          connection with this Agreement shall
                                          first be attempted to be resolved
                                          amicably through mutual discussion.
                                        </li>
                                        <li>
                                          Failing amicable settlement, disputes
                                          shall be referred to Arbitration under
                                          the Arbitration and Conciliation Act,
                                          1996.
                                        </li>
                                        <li>
                                          The Arbitrator shall be appointed
                                          solely by the Company, and the
                                          decision of the Arbitrator shall be
                                          final and binding.
                                        </li>
                                        <li>
                                          The courts at Bangalore, Karnataka,
                                          shall have exclusive jurisdiction.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          8
                                        </span>
                                        Governing Law
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          This Agreement shall be governed by
                                          and construed in accordance with the
                                          laws of India.
                                        </li>
                                      </ul>
                                      <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                        <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                          9
                                        </span>
                                        Cancellation & Refund Policy –
                                        Lifeonplus Channel Partner
                                      </h3>
                                      <ul className="list-disc pl-6 space-y-2">
                                        <li>
                                          In case a Channel Partner wishes to
                                          cancel or terminate their association
                                          with Lifeonplus Technologies, a
                                          written notice must be provided at
                                          least 30 (thirty) days prior to the
                                          intended date of cancellation.
                                        </li>
                                        <li>
                                          Upon completion of the 30-day notice
                                          period and subject to verification of
                                          accounts and compliance with all
                                          applicable terms:
                                        </li>
                                        <li>
                                          The eligible refund amount shall be
                                          processed within 31 (thirty-one)
                                          working days from the effective date
                                          of cancellation.
                                        </li>
                                        <li>
                                          The Company will deduct: *Applicable
                                          GST
                                          <li>
                                            {" "}
                                            *Any Referral Incentives
                                          </li>{" "}
                                          <li>
                                            {" "}
                                            *Any Business Promotion Incentives
                                          </li>{" "}
                                          <li>
                                            {" "}
                                            *Any other dues, liabilities, or
                                            outstanding amounts payable to the
                                            Company
                                          </li>
                                        </li>
                                        <li>
                                          The final refundable amount shall be
                                          calculated after adjusting the above
                                          deductions, and the Company’s decision
                                          regarding the settlement amount shall
                                          be final and binding.
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4">
                                  <label className="flex items-center text-sm md:text-sm font-['Poppins'] max-w-full">
                                    <Field
                                      type="checkbox"
                                      name="agreement"
                                      className="mr-2 h-4 w-4"
                                    />
                                    <span>
                                      I agree to the Terms and Conditions{" "}
                                      <span className="text-red-500 font-semibold">
                                        <span className="text-red-500">*</span>
                                      </span>{" "}
                                      and understand that this investment
                                      secures exclusive rights for the selected
                                      region, subject to verification by
                                      LifeOnPlus. I confirm all information
                                      provided is true to the best of my
                                      knowledge.
                                    </span>
                                  </label>
                                  <ErrorMessage
                                    name="agreement"
                                    component="div"
                                    className="text-red-500 text-xs mt-1"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          <div className="flex justify-between mt-4">
                            {step > 0 && (
                              <Button
                                onClick={() => setStep(step - 1)}
                                disabled={isSubmitting}
                                className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
                              >
                                Back
                              </Button>
                            )}
                            <Button
                              type="submit"
                              disabled={
                                isSubmitting ||
                                (step === 4 && !values.agreement)
                              }
                              className="px-4 py-2 bg-[#00C70D] text-white font-['Poppins'] rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
                            >
                              {step === formSteps.length - 1
                                ? "Submit"
                                : "Next"}
                            </Button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <>
      <Modal show={showModal} onHide={handleClose} centered size="xl">
        <Modal.Body className="p-0">
          <div className="flex flex-col sm:flex-row min-h-[600px] sm:min-h-[842px]">
            {!isPlusMode && (
              <div className="w-full sm:w-1/4 bg-gradient-to-b from-white to-green-500 px-6 py-10 flex flex-col">
                <div className="mb-6">
                  <p className="text-md text-center font-semibold my-4">
                    Steps to register as channel partner
                  </p>
                  <img
                    src="/lifeon-new.svg.jpeg"
                    alt="LifeOnPlus Logo"
                    className="w-24 mx-auto mb-4"
                  />
                  <p className="text-sm">
                    {" "}
                    Follow the Steps to Register as a Channel Partner, to start
                    your jouney with Lifeonplus!
                  </p>
                </div>

                <div className="space-y-6">
                  {steps.map((label, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 cursor-pointer"
                      onClick={() =>
                        isStepCompleted(index) && setCurrentStep(index)
                      }
                    >
                      <div className="relative">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            index <= currentStep
                              ? "bg-green-600 text-white"
                              : isStepCompleted(index)
                                ? "bg-green-400 text-white"
                                : "bg-white text-green-600 border border-green-600"
                          }`}
                        >
                          {isStepCompleted(index) && index > currentStep ? (
                            <FaCheckCircle />
                          ) : (
                            index + 1
                          )}
                        </div>
                        {index < steps.length - 1 && (
                          <div
                            className={`absolute left-1/2 w-0.5 h-6 top-full ${
                              index < currentStep
                                ? "bg-green-600"
                                : "bg-gray-300"
                            }`}
                          ></div>
                        )}
                      </div>
                      <span
                        className={`text-sm ${
                          index === currentStep
                            ? "font-semibold text-green-800"
                            : "text-gray-800"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className={`w-full ${isPlusMode ? "" : "sm:w-3/4"} bg-white`}>
              <StepContent step={currentStep} />
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <ApplicationFormModal />
      <Modal
        show={showThankYou}
        onHide={handleCloseThankYou}
        centered
        dialogClassName="modal-90w md:modal-lg"
      >
        <Modal.Body className="p-4 md:p-6">
          <div className="relative bg-white rounded-lg text-center py-6">
            <button
              onClick={handleCloseThankYou}
              className="absolute top-2 right-2 bg-transparent border-0"
            >
              <FaTimes size={16} color="#666" />
            </button>
            <h2 className="text-lg md:text-xl font-bold text-green-600">
              Thank You for Your Application!
            </h2>
            <p className="text-gray-500 text-sm md:text-sm mt-2">
              Your application has been successfully submitted. We will review
              your details and get back to you soon.
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ChannelPartnerSignUp;
